import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import DashboardLayout from "../../layout";
import { useForm, Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import {
  createTransaction,
  CreateTransactionPayload,
} from "../../services/manualBooking";
import { useNavigate } from "react-router-dom";
import { LoadingState } from "../../components/LoadingState";
import BackButton from "components/BackButton";

const schema = yup.object({
  amount: yup.string().required("Amount is required"),
  type: yup.string().required("Type is required"),
  transactionDate: yup.mixed().required("Transaction Date is required"),
  status: yup.string().required("Status is required"),
  paymentReference: yup.string().required("Payment Reference is required"),
});

const TransactionCreationPage = () => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      amount: "",
      type: "manual",
      transactionDate: new Date(),
      status: "success",
      paymentReference: "",
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: (data: any) => {
      const payload: CreateTransactionPayload = {
        amount: data.amount,
        type: data.type,
        transactionDate: new Date(data.transactionDate).toISOString(),
        status: data.status,
        paymentReference: data.paymentReference,
      };
      return createTransaction(payload);
    },
    onSuccess: (data) => {
      // Assuming the API returns the created transaction object with an _id or id
      const transactionId = data?.data?._id || data?._id || data?.id;

      if (transactionId) {
        navigate("/dashboard/manual-bookings/add", {
          state: { transactionId },
        });
      } else {
        // Fallback if ID structure is different, just navigate but log warning
        console.warn("Transaction ID not found in response", data);
        navigate("/dashboard/manual-bookings/add");
      }
    },
  });

  const onSubmit = (data: any) => {
    mutate(data);
  };

  return (
    <DashboardLayout nextSideBar>
      {isPending && <LoadingState />}
      <Box sx={{ width: "100%", px: { xs: 0, sm: 2 }, py: 2 }}>
        <Box
          sx={{
            background: "white",
            mt: "-50px",
            mx: { xs: "-36px", sm: "-22px" },
            mb: "30px",
          }}
        >
          <BackButton />
        </Box>

        <Box mb={3} mt={2}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            Create Transaction
          </Typography>
          <Typography variant="body2" color="#475467">
            Create a transaction before adding a manual booking
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(error as any)?.response?.data?.message ||
              error.message ||
              "An error occurred while creating the transaction"}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            bgcolor: "white",
            py: 3,
            px: { xs: 1.5, sm: 3 },
            borderRadius: "12px",
            border: "1px solid #EAECF0",
            maxWidth: "800px",
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Amount"
                    fullWidth
                    error={!!errors.amount}
                    helperText={errors.amount?.message as string}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Type"
                    fullWidth
                    disabled
                    error={!!errors.type}
                    helperText={errors.type?.message as string}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="paymentReference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payment Reference"
                    fullWidth
                    error={!!errors.paymentReference}
                    helperText={errors.paymentReference?.message as string}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  name="transactionDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Transaction Date"
                      value={field.value}
                      onChange={(date) => field.onChange(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.transactionDate,
                          helperText: errors.transactionDate?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/dashboard/manual-bookings")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isPending}
                  sx={{
                    bgcolor: "#0b1c12",
                    "&:hover": { bgcolor: "#08150e" },
                  }}
                >
                  Create Transaction
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default TransactionCreationPage;
